import { Menu as BaseMenu } from "@base-ui/react/menu";
import classNames from "classnames";
import * as React from "react";

import styles from "./DropdownMenu.module.css";

export type DropdownMenuProps = React.ComponentProps<typeof BaseMenu.Root>;

export type DropdownMenuTriggerProps = React.ComponentProps<
  typeof BaseMenu.Trigger
>;

export type DropdownMenuContentProps = Omit<
  React.ComponentProps<typeof BaseMenu.Popup>,
  "className"
> & {
  /** Extra class(es) for the popup surface, e.g. a width. */
  className?: string;
  /**
   * Side of the trigger the menu opens on. Flips when there is no room.
   * @default "bottom"
   */
  side?: "top" | "bottom" | "left" | "right";
  /** @default "start" */
  align?: "start" | "center" | "end";
};

export type DropdownMenuItemProps = Omit<
  React.ComponentProps<typeof BaseMenu.Item>,
  "className"
> & {
  /** Shown before the content, sized and colored to match every menu. */
  icon?: React.ReactNode;
  /**
   * `"danger"` marks an action that destroys something.
   * @default "default"
   */
  variant?: "default" | "danger";
  className?: string;
};

function Root(props: DropdownMenuProps) {
  return <BaseMenu.Root {...props} />;
}

/**
 * The element that opens the menu, passed as `render`. Base UI merges the
 * menu wiring onto it: toggle on click, `aria-haspopup`, `aria-expanded`.
 */
function Trigger(props: DropdownMenuTriggerProps) {
  return <BaseMenu.Trigger {...props} />;
}

function Content({
  className,
  side = "bottom",
  align = "start",
  ...props
}: DropdownMenuContentProps) {
  return (
    <BaseMenu.Portal>
      {/* 4 = --spacing-2xs; JS props can't read CSS variables. */}
      <BaseMenu.Positioner
        side={side}
        align={align}
        sideOffset={4}
        collisionPadding={8}
        className={styles.positioner}
      >
        <BaseMenu.Popup
          className={classNames(styles.popup, className)}
          {...props}
        />
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

/**
 * One action. The item owns how it looks as it is used (highlighted,
 * disabled, destructive); its children are its content, so a menu that needs
 * more than a label lays that out itself and passes a `className`.
 */
function Item({
  icon,
  variant = "default",
  className,
  children,
  ...props
}: DropdownMenuItemProps) {
  return (
    <BaseMenu.Item
      className={classNames(styles.item, className)}
      data-variant={variant}
      {...props}
    >
      {icon !== undefined && (
        <span className={styles.icon} aria-hidden>
          {icon}
        </span>
      )}
      {children}
    </BaseMenu.Item>
  );
}

function Separator({ className }: { className?: string }) {
  return (
    <BaseMenu.Separator className={classNames(styles.separator, className)} />
  );
}

/**
 * A menu of actions opened from a button — the design-system wrapper around
 * Base UI's Menu, shaped like kumo's `DropdownMenu`. Arrow keys move between
 * items, typing jumps to one, and choosing an item runs it and closes the
 * menu. `DropdownMenu` is itself `DropdownMenu.Root`.
 *
 * @example
 * ```tsx
 * <DropdownMenu>
 *   <DropdownMenu.Trigger render={<RobotoButton>Actions</RobotoButton>} />
 *   <DropdownMenu.Content>
 *     <DropdownMenu.Item icon={<Pencil />} onClick={rename}>Rename</DropdownMenu.Item>
 *     <DropdownMenu.Separator />
 *     <DropdownMenu.Item icon={<Trash2 />} variant="danger" onClick={remove}>
 *       Delete
 *     </DropdownMenu.Item>
 *   </DropdownMenu.Content>
 * </DropdownMenu>
 * ```
 */
export const DropdownMenu = Object.assign(Root, {
  Root,
  Trigger,
  Content,
  Item,
  Separator,
});
